import { cn } from "@gymflow/helpers";
import { Field, FieldProps, FormikProps } from "formik";
import { DateTime } from "luxon";

import {
  DatePicker,
  DatePickerProps,
} from "../../molecules/DatePicker/DatePicker";
import { ErrorMessage } from "..";
import Label from "../base/Label";
import { Path } from "./types";

type FormSingleDatePickerProps<T> = {
  name: Path<T>;
  label?: string;
  isClearable?: boolean;
  isRequired?: boolean;
  isDisabled?: boolean;
  containerClassName?: string;
  useIsoString?: boolean;
  useDateOnly?: boolean;
  isOptional?: boolean;
} & Pick<
  DatePickerProps<boolean>,
  | "modifiers"
  | "disabled"
  | "hidden"
  | "showQuickNavigation"
  | "hideNavigation"
  | "captionLayout"
  | "components"
  | "manualApply"
>;

export const FormSingleDatePicker = <T,>({
  name,
  label,
  isRequired,
  containerClassName,
  useIsoString,
  useDateOnly,
  isOptional,
  ...props
}: FormSingleDatePickerProps<T>) => (
  <Field name={name}>
    {({
      field,
      meta: { touched, error },
    }: FieldProps<string | Date | undefined, FormikProps<T>>) => (
      <div className={cn("flex flex-col gap-1.5", containerClassName)}>
        <Label label={label} isRequired={isRequired} isOptional={isOptional} />
        <DatePicker
          {...props}
          label={
            field.value
              ? useIsoString
                ? DateTime.fromISO(field.value as string).toLocaleString(
                    DateTime.DATE_MED_WITH_WEEKDAY,
                  )
                : DateTime.fromJSDate(field.value as Date).toLocaleString(
                    DateTime.DATE_MED_WITH_WEEKDAY,
                  )
              : undefined
          }
          mode="single"
          selected={
            field.value
              ? useIsoString
                ? DateTime.fromISO(field.value as string).toJSDate()
                : (field.value as Date)
              : undefined
          }
          handleDateSave={(newValue: Date | undefined) => {
            let value: Date | string | undefined = newValue;
            if (newValue && useIsoString) {
              value = useDateOnly
                ? DateTime.fromJSDate(newValue).toISODate()!
                : DateTime.fromJSDate(newValue).toISO()!;
            }
            field.onChange({
              target: {
                name,
                value,
              },
            });
          }}
        />
        {touched && error && <ErrorMessage text={error} />}
      </div>
    )}
  </Field>
);
